import { useEffect, useState } from "react";
import classNames from "classnames";
import { Button } from "components/atoms/Button";
import { ViewProgressSummaryCard } from "components/organisms/ViewProgressSummaryCard";
import { PrepInformation } from "components/molecules/PrepInformation";
import { useAppDispatch, useAppSelector, useScreenSize } from "hooks";
import {
  MOBILE_WIDTH,
  TAB_WIDTH,
  TAB_LARGE_WIDTH,
  MCAT_TEST_TYPE,
  GMAT_TEST_TYPE,
  GMAT_MATERIAL,
} from "utils/constants";
import { GMATMaterialModal, Header } from "components/molecules";
import { DropDown, Typography } from "components/atoms";
import { ProgressSummaryMobile } from "components/organisms/ProgressSummaryMobile";
import { ViewAllSummaryMobile } from "components/organisms/ViewAllSummaryMobile";
import { AiCardSection } from "components/organisms/AiCardSection";
import { SelfPacedCourseworks } from "components/organisms/SelfPacedCourseworks";
import { decodeToken } from "utils/functions/decodeToken";
import { useLocation, useNavigate } from "react-router-dom";
import { setToken } from "state/reducers/AuthSlice";
import { Spinner } from "components/atoms/Icon";
import { SelfPacedLiveSessions } from "components/organisms/SelfPacedLiveSessions";
import { PersonalizedBanner } from "components/molecules/PersonalizedBanner";
import { useSegmentReFetch } from "context";
import { ProgressCardSection } from "components/organisms/ProgressCardSection";
import { ViewProgressSummaryInnerCardGroup } from "components/organisms/ViewProgressSummaryInnerCardGroup";
import useGoogleAnalytics from "hooks/useGoogleAnalytics";
import { feature_toggle } from "featureToggle";
import { NotificationModal } from "components/molecules/NotificationModal";
import "./index.scss";
import { setIsShowACTPopup } from "state/reducers/layoutSlice";
import { useLazyGetContentQuery, useGetGMACMaterialsQuery } from "services/homeApi";
import { api } from "services/api";
import { mcatChatApi } from "services/mcatChat/mcatChatApi";

interface IProps {
  theme: "light" | "dark";
}

export const SelfPacedHome = ({ theme }: IProps) => {
  const [isProgressShow, setIsProgressShow] = useState(false);
  const [isPrepInfoShow, setIsPrepInfoShow] = useState(false);
  const [isAiShow, setIsAiShow] = useState(false);
  const [isShowMobileTestDetails, setIsShowMobileTestDetails] = useState(false);
  const [isShowMobileViewAllSummary, setIsShowMobileViewAllSummary] =
    useState(false);
  const [clickedGraphNameMobile, setClickedGraphNameMobile] = useState<
    undefined | string
  >();
  const [deropDownSelection, setDropDownSelection] = useState<
    null | string | { [key: number]: string }
  >({ "0": "All Subjects" });

  const [showGMATMaterialModal, setShowGMATMaterialModal] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notificationContent, setNotificationContent] = useState("");
  const [isNotificationLoading, setIsNotificationLoading] = useState(false);

  const [bannerContent, setBannerContent] = useState("");
  const [isBannerLoading, setIsBannerLoading] = useState(false);
  const [prepInfoButtonText, setPrepInfoButtonText] = useState("");

  const navigate = useNavigate();

  const screen = useScreenSize();

  const payload = decodeToken();

  const location = useLocation();

  const { setSubject, setPage } = useSegmentReFetch();

  const { token } = useAppSelector((state) => state.auth);
  const { isShowACTPopup } = useAppSelector((state) => state.layout);

  const dispatch = useAppDispatch();

  const { trackEvent } = useGoogleAnalytics();

  const [getContent] = useLazyGetContentQuery();

  const testtype =
    payload && payload.hasOwnProperty("testtype") && payload.testtype;

  const userName = payload ? payload.username : "unknown";

  const productCode = payload ? payload.productcode : null;

  const isGMAT = testtype === GMAT_TEST_TYPE;
  
  const { data: gmacMaterialsData } = useGetGMACMaterialsQuery(undefined, {
    skip: !isGMAT,
  });

  const hasGMACMaterials = gmacMaterialsData?.Materials && 
    Array.isArray(gmacMaterialsData.Materials) && 
    gmacMaterialsData.Materials.length > 0;

  const [isShowOverallProgressSummary] = useState(
    payload &&
      payload.hasOwnProperty("showoverallprogresssummary") &&
      payload.showoverallprogresssummary.toLowerCase() === "true"
  );

  const [isShowStudyRecommendation] = useState(
    payload &&
      payload.hasOwnProperty("showstudyrecommendations") &&
      payload.showstudyrecommendations.toLowerCase() === "true"
  );

  const [isShowPerformanceSummary] = useState(
    payload &&
    payload.hasOwnProperty("showperformancesummary") &&
    payload.showperformancesummary.toLowerCase() === "true"
  );

  const handleGMATMaterialModalClose = () => {
    setShowGMATMaterialModal(false);
  };

  const shouldShowNotificationModal = () => {
    if (payload && 
        payload.hasOwnProperty("shownotificationpopup") && 
        payload.shownotificationpopup.toLowerCase() === "false") {
      return false;
    }
    const wasOnJumpPage = isShowACTPopup;
    
    const hasSessionDismissed = sessionStorage.getItem("notificationDismissed") === "true";
    
    return wasOnJumpPage && !hasSessionDismissed;
  };

  const fetchNotificationContent = async (contentKey: string) => {
    try {
      setIsNotificationLoading(true);
      const response = await getContent({ contentkey: contentKey }).unwrap();
      
      if (response && response.TextData) {
        setNotificationContent(response.TextData);
      } else {
        // Fallback to default content if API doesn't return TextData
        setNotificationContent("Welcome to your Student Dashboard");
      }
    } catch (error) {
      console.error("Failed to fetch notification content:", error);
      // Fallback to default content on error
      setNotificationContent("Welcome to your Student Dashboard");
    } finally {
      setIsNotificationLoading(false);
    }
  };

  
  const handleNotificationModalClose = () => {
    setIsNotificationModalOpen(false);
    sessionStorage.setItem("notificationDismissed", "true");
    dispatch(setIsShowACTPopup(false));
  };

  useEffect(() => {
    if (payload) {
      if (shouldShowNotificationModal()) {
        const contentKey = payload.hasOwnProperty("notificationcmskey") && payload.notificationcmskey
          ? payload.notificationcmskey
          : null;
        
        if (contentKey) {
          // Fetch content from API using the content key
          if (!isNotificationLoading && !notificationContent) {
            fetchNotificationContent(contentKey);
          }
        } else {
          // Fallback to default content if no content key is provided
          setNotificationContent("Welcome to your Student Dashboard");
        }
        setIsNotificationModalOpen(true);
      }
    }
  }, [payload, isShowACTPopup, isNotificationLoading, notificationContent]);

  useEffect(() => {
    if (productCode) {
            if (!prepInfoButtonText) {
          getContent({ contentkey: `${productCode}_Banner` })
          .unwrap()
          .then((res) => {
              if (res && res.TextData) {
                setBannerContent(res.TextData);
              setPrepInfoButtonText("View additional prep info");
            }
          })
          .catch(() => {});
      }
    }
  }, [productCode, token]);

  const handleCardClick = (cardType: string) => {
    if (cardType === "doughnut") {
      setIsProgressShow(true);
      setIsPrepInfoShow(false);
    } else {
      setIsProgressShow(false);
      setIsPrepInfoShow(true);
    }

    switch (cardType) {
      case "doughnut":
        setIsProgressShow(true);
        setIsPrepInfoShow(false);
        setIsAiShow(false);
        break;
      case "horizontal":
        setIsProgressShow(false);
        setIsPrepInfoShow(true);
        setIsAiShow(false);
        break;
      case "ai":
        setIsProgressShow(false);
        setIsPrepInfoShow(false);
        setIsAiShow(true);

        feature_toggle.googleTagManager.isEnable === true && trackEvent("button_click", "view recommendation widget (Big)")

        break;
      default:
        break;
    }
  };

  const handleInnerCardBtnClick = (cardType: string) => {
    if (cardType === "ai") {
      setIsProgressShow(false);
      setIsPrepInfoShow(false);
      setIsAiShow(true);
      feature_toggle.googleTagManager.isEnable === true && trackEvent("button_click", "view recommendation widget (Small)")
    } else if (cardType === "score") {
      setIsProgressShow(false);
      setIsPrepInfoShow(true);
      setIsAiShow(false);
    } else {
      setIsProgressShow(true);
      setIsPrepInfoShow(false);
      setIsAiShow(false);
    }
  };

  const handlePreInfoClick = () => {
    window.open("/view-prep", "__blank");
  };

  useEffect(() => {
    if (location.search !== "") {
      const newToken = location.search.split("=")[1];
      
      // Check if this is a new token before updating
      const storedToken = localStorage.getItem("lastToken");
      const isNewToken = storedToken !== newToken;
      
      dispatch(setToken(newToken));
      
      // Only set jump page flag and clear dismissal if it's a new token
      if (isNewToken) {
        // Clear all cached API data from the previous product
        dispatch(api.util.resetApiState());
        dispatch(mcatChatApi.util.resetApiState());

        dispatch(setIsShowACTPopup(true));
        sessionStorage.removeItem("notificationDismissed");

        // Update stored token after all checks
        localStorage.setItem("lastToken", newToken);
      }
    }
  }, [location.search]);

  useEffect(() => {
    if (location && location.state && location.state.fromStudyRecommendationTool === true) {
      setIsAiShow(true);
      window.history.replaceState({}, "");
    }
  }, [location]);

  useEffect(() => {
    if (screen.width > MOBILE_WIDTH) {
      setIsShowMobileTestDetails(false);
      setIsShowMobileViewAllSummary(false);
    }
  }, [screen.width]);

  useEffect(() => {
    if(deropDownSelection && setSubject) {
      setSubject(parseInt(Object.keys(deropDownSelection)[0]))
      setPage(1)
    }
  }, [deropDownSelection, setSubject]);

  if (token !== "") {
    return isShowMobileTestDetails ? (
      <ProgressSummaryMobile
        setIsShowMobileTestDetails={setIsShowMobileTestDetails}
        clickedGraphNameMobile={clickedGraphNameMobile}
        theme={theme}
      />
    ) : isShowMobileViewAllSummary ? (
      <ViewAllSummaryMobile
        setIsShowMobileViewAllSummary={setIsShowMobileViewAllSummary}
        theme={theme}
      />
    ) : (
      <>
        <NotificationModal
          isOpen={isNotificationModalOpen}
          handleOnClose={handleNotificationModalClose}
          theme={theme}
          notificationContent={notificationContent}
          isLoading={isNotificationLoading}
        />

      <div className="self-paced-home-page-main-wrapper">
        <div className={classNames("self-paced-home-header-section")}>
          <Header
            isMobile={screen.width < MOBILE_WIDTH}
            theme={theme}
            leftContentLabel={`Welcome, ${userName}!`}
            isTab={screen.width <= TAB_LARGE_WIDTH}
          />
        </div>

        <PersonalizedBanner
          theme={theme}
          content={bannerContent}
          isLoading={isBannerLoading}
        />
        
        <div className={classNames("self-paced-home-page-top-label-section")}>
          <Button
            type="filter"
            label={
              prepInfoButtonText
                ? prepInfoButtonText
                : screen.width < MOBILE_WIDTH
                  ? "View Your Prep Information"
                  : "Click here to start! View your prep info"
            }
            onClick={handlePreInfoClick}
            styles="custom-typography-style"
            icon={false}
            btnId="dashboard-self-paced-view-prep-info-btn"
          />
        </div>
        <div
          className={classNames("self-paced-home-page-progress-card-section")}
        >
          {isProgressShow ? (
            <ViewProgressSummaryCard
              setIsShowMobileTestDetails={setIsShowMobileTestDetails}
              setIsShowMobileViewAllSummary={setIsShowMobileViewAllSummary}
              setClickedGraphNameMobile={setClickedGraphNameMobile}
              theme={theme}
            />
          ) : isPrepInfoShow ? (
            <PrepInformation
              isMobile={screen.width < MOBILE_WIDTH}
              isTab={screen.width <= TAB_WIDTH}
              theme={theme}
            />
          ) : isAiShow ? (
            <AiCardSection theme={theme} />
          ) : (
            <ProgressCardSection
              theme={theme}
              onClick={handleCardClick}
            />
          )}
        </div>
        <div
          className={classNames({
            "view-progress-card-inner-card-group": true,
            "is-hide-ai-nav-card": !isShowStudyRecommendation,
            "is-hide-progress-summary-nav-card": !isShowOverallProgressSummary,
            "is-hide-performance-summary-nav-card": !isShowPerformanceSummary,
          })}
        >
          {[isShowOverallProgressSummary, isShowPerformanceSummary, isShowStudyRecommendation]
            .filter(Boolean).length > 1 ?
            (isPrepInfoShow || isProgressShow || isAiShow ? (
            <ViewProgressSummaryInnerCardGroup
              btnLabel={
                isPrepInfoShow
                  ? "View Progress Summary"
                  : isAiShow
                    ? "View Progress Summary"
                    : "View Scores"
              }
              cardLabel={
                isPrepInfoShow
                  ? "Overall Progress Summary"
                  : isAiShow
                    ? "Overall Progress Summary"
                    : "Test Scores & Performance"
              }
              secondaryBtnLabel={
                isPrepInfoShow
                  ? "View Recommendations"
                  : isAiShow
                    ? "View Scores"
                    : "View Recommendations"
              }
              secondaryCardLabel={
                isPrepInfoShow
                  ? "Study Recommendations"
                  : isAiShow
                    ? "Test Scores & Performance"
                    : "Study Recommendations"
              }
              thirdBtnLabel={
                  isProgressShow
                    ? "View Recommendations"
                    : isAiShow
                      ? "View Progress Summary"
                      : "View Recommendations"
                }
                thirdCardLabel={
                  isProgressShow
                    ? "Study Recommendations"
                    : isAiShow
                      ? "Overall Progress Summary"
                      : "Study Recommendations"
                }
              onClick={handleInnerCardBtnClick}
            />
          ) : null ) : null}
        </div>
        <SelfPacedLiveSessions theme={theme} />
        <div className={classNames("self-paced-home-page-title-section")}>
          <Typography variant="h3Bold" label="Coursework" />
          {testtype === MCAT_TEST_TYPE ? (
            <div className={classNames("subjects-drop-down")} id="self-paced-subjects-drop-down">
              <DropDown
                data={[
                  "All Subjects",
                  "Biology",
                  "CARS",
                  "General Chemistry",
                  "Organic Chemistry",
                  "Physics",
                  "Psych/Soc",
                ]}
                setDropDownSelection={setDropDownSelection}
                defaultSelected={deropDownSelection}
              />
            </div>
          ) : null}
          {isGMAT && hasGMACMaterials ? (
            <Button
              icon={false}
              type="score"
              label="Access Your Official GMAC Material"
              onClick={() => {
                if(screen.width <= MOBILE_WIDTH) {
                  navigate(`${GMAT_MATERIAL}`)
                } else {
                  setShowGMATMaterialModal(true)
                }
              }}
            />
          ) : null}
          <GMATMaterialModal
            isOpen={showGMATMaterialModal}
            handleOnClose={handleGMATMaterialModalClose}
            theme={theme}
            materials={gmacMaterialsData?.Materials}
          />
        </div>
        <SelfPacedCourseworks theme={theme} />
      </div>
      </>
    );
  } else {
    return <Spinner theme={theme} />;
  }
};
